import { listOfCity, listOfCountry, listOfState } from "Api/common";
import { upload_image } from "Api/profile";
import { BaseImageURL } from "Api/services";
import { editProfile, viewProfile } from "Api/UserApi";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { BaseSelect } from "Components/Base/BaseSelect";
import avatar1 from "../../assets/images/users/avatar-1.jpg";
import { useNavigate, useParams } from "react-router-dom";
import {
  checkStatusCodeSuccess,
  contactLabel,
  loginLabels,
  profileTitle,
  saveButton,
} from "Components/constants/Common";
import {
  cancelLabel,
  editLabel,
  NameLabel,
  RoleLabel,
} from "Components/constants/Role";
import {
  AlternatePhoneNumberLabel,
  CityLabel,
  CountryLabel,
  EmailLabel,
  PhoneNumberLabel,
  PostalCodeLabel,
  StateLabel,
  StreetLabel,
} from "Components/constants/Staff";
import {
  InputPlaceHolder,
  numberRegex,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import * as Yup from "yup";
import { HttpStatusCodes } from "helpers/Enums/Enums";
import { Http2ServerResponse } from "http2";
import { useDispatch } from "react-redux";
import {
  profileSuccess,
  updateProfileImage,
} from "../../slices/auth/profile/reducer";

interface profileDataInterface {
  id: number;
  name?: string;
  email: string;
  contact_no?: string;
  alternative_contact_no?: string;
  position?: {
    id?: number;
    role?: string;
  };
  address: {
    id?: number;
    address?: string;
    postal_code?: number;
    primaryCountry?: {
      id?: number;
      country_name?: string;
    };
    primaryState?: {
      id?: number;
      state_name?: string;
    };
    primaryCity?: {
      id?: number;
      city_name?: string;
    };
  };
}

const Profile: React.FC = () => {
  document.title = profileTitle;
  const dispatch = useDispatch();

  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState<profileDataInterface>();
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageLoader, setImageLoader] = useState<boolean>(false);
  const [profileLoader, setProfileLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const handleImageError = () => {
    setImageError(true);
  };

  const getImageSource = () => {
    if (imageError || !selectedImage) {
      return avatar1;
    }
    return `${BaseImageURL}${selectedImage}`;
  };
  console.log(`${BaseImageURL}${selectedImage}`);

  const toggleEdit = () => {
    setIsEditing((prevState) => !prevState);
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(validationMessages.required(NameLabel)),
    contact_no: Yup.string()
      .required(validationMessages.required(PhoneNumberLabel))
      .matches(
        numberRegex,
        validationMessages.contactLength(PhoneNumberLabel, 10)
      ),
    alternative_contact_no: Yup.string().matches(
      numberRegex,
      validationMessages.contactLength(AlternatePhoneNumberLabel, 10)
    ),
  });

  const uploadImages = async (files: FileList) => {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }
    try {
      const data = await upload_image(formData);
      return data;
    } catch (error) {
      return null;
    }
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.currentTarget.files;
    if (files) {
      setImageLoader(true);
      const uploadResponse = await uploadImages(files);
      setImageLoader(false);

      if (
        uploadResponse &&
        uploadResponse.data &&
        uploadResponse.data.length > 0
      ) {
        const imageName = uploadResponse.data[0];
        const payload = { profile_image: imageName };

        setProfileLoader(true);
        editProfile(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success("Profile Image Updated Successfully!");

              setSelectedImage(imageName);
              setImageError(false);

              dispatch(updateProfileImage(imageName));

              const updatedProfile = {
                ...profileData,
                profile_image: imageName,
              };
              sessionStorage.setItem(
                "userProfile",
                JSON.stringify(updatedProfile)
              );
            } else {
              toast.error(res?.message);
            }
          })
          .catch((error) => {
            toast.error(error?.response?.data?.message || error?.message);
          })
          .finally(() => setProfileLoader(false));
      } else {
        toast.error("Image upload failed or no image returned.");
      }
    }
  };
  const fetchCountry = () => {
    listOfCountry({})
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setCountries(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.country_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setProfileLoader(false);
      });
  };

  const fetchState = (id: number) => {
    listOfState({ condition: { country_id: id } })
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setStates(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.state_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err: any) => {
        setStates([]);
        fetchCity(null);
        return err;
      })
      .finally(() => {
        setProfileLoader(false);
      });
  };

  const fetchCity = (id: any) => {
    listOfCity({ condition: { state_id: id } })
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setCity(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.city_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        setCity([]);
        return err;
      })
      .finally(() => {
        setProfileLoader(false);
      });
  };
  const [initialValues, setInitialValues] = useState({
    name: "",
    contact_no: "",
    alternative_contact_no: "",
    address: "",
    city_id: null,
    state_id: null,
    country_id: null,
    postal_code: null,
    email: "",
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        name: values.name,
        contact_no: values.contact_no,
        alternative_contact_no: values.alternative_contact_no,
        address: values.address,
        city_id: values.city_id || null,
        state_id: values.state_id || null,
        country_id: values.country_id || null,
        postal_code: values.postal_code ? Number(values.postal_code) : null,
      };

      setProfileLoader(true); // Start profile loader
      editProfile(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success("Profile Updated Successfully !");
            viewProfileData();
            setIsEditing(false);
            dispatch(
              profileSuccess({ status: res?.statusCode, data: res?.data })
            );
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          if (error?.status === 401) {
            toast.error(
              "Access Denied! You do not have permission to access this module."
            );
          } else {
            toast.error(error?.response?.data?.message || error?.message);
          }
        })
        .finally(() => setProfileLoader(false));
    },
  });

  const viewProfileData = () => {
    setProfileLoader(true);
    viewProfile()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const newValues = {
            name: res?.data?.name || "",
            contact_no: res?.data?.contact_no || "",
            alternative_contact_no: res?.data?.alternative_contact_no || "",
            address: res?.data?.address?.address || "",
            city_id: res?.data?.address?.primaryCity?.id || 0,
            state_id: res?.data?.address?.primaryState?.id || 0,
            country_id: res?.data?.address?.primaryCountry?.id || 0,
            postal_code: res?.data?.address?.postal_code || "",
            email: res?.data?.email || "",
          };
          setInitialValues(newValues);
          formik.setValues(newValues);
          setProfileData(res?.data);
          setSelectedImage(res?.data.profile_image);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || error?.message);
      })
      .finally(() => setProfileLoader(false));
  };

  const hasFormChanged = () => {
    return Object.keys(initialValues).some(
      (key) =>
        formik.values[key as keyof typeof initialValues] !==
        initialValues[key as keyof typeof initialValues]
    );
  };
  useEffect(() => {
    viewProfileData();
    fetchCountry();
  }, []);

  useEffect(() => {
    if (profileData?.address?.primaryCountry?.id) {
      fetchState(Number(profileData?.address?.primaryCountry?.id));
    }
    if (profileData?.address?.primaryState?.id) {
      fetchCity(Number(profileData?.address?.primaryState?.id));
    }
  }, [profileData]);

  return (
    <div className="page-content">
      <div className="flex-grow-1">
        <div className="cont d-flex justify-content-end me-3">
          <button
            className="btn btn-danger d-flex justify-content-end"
            onClick={() => navigate("/dashboard")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="bg-white m-0">
          <div className="profile-card">
            <div className="row">
              <div className="profile-left col-sm-12 col-md-3 d-flex align-items-center justify-content-center">
                <div className="d-flex flex-column align-items-center justify-content-start ">
                  <div className="position-relative d-inline-block">
                    {imageLoader ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: 150, width: 150 }}
                      >
                        <Spinner
                          style={{ width: "3rem", height: "3rem" }}
                          color="dark"
                        />
                      </div>
                    ) : (
                      <img
                        src={getImageSource()}
                        alt={profileData?.name || "Profile"}
                        className="rounded bg-gradient bordered border-dark"
                        height={150}
                        width={150}
                        onError={handleImageError}
                      />
                    )}
                    <h5 className="text-center mt-3">
                      <strong>{RoleLabel}: </strong>
                      {profileData?.position?.role}
                    </h5>
                    <label
                      htmlFor="image-upload"
                      className="position-absolute bottom-0 end-0 translate-middle-y cursor-pointer mb-5"
                      style={{ marginRight: "-10px" }}
                    >
                      <i className="mdi mdi-camera text-white fs-5 bg-success p-2 rounded-circle" />
                    </label>
                    <input
                      type="file"
                      id="image-upload"
                      className="d-none"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>
                </div>
              </div>

              <div className="profile-right col-sm-12 col-md-9">
                <div className="d-flex justify-content-end align-items-start gap-3 mt-2">
                  <BaseButton
                    color="light"
                    className={`border-0 rounded btn-edit px-3 mt-0 ${
                      isEditing ? "d-none" : ""
                    } edit-icon`}
                    onClick={toggleEdit}
                    size={"sm"}
                  >
                    <span className="tooltip-container">
                      <i className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"></i>
                      <span className="tooltip-text">{editLabel}</span>
                    </span>
                  </BaseButton>
                </div>
                <h5>{loginLabels.personalDetails}</h5>
                <div className="border-bottom mb-2" />
                <div className="row">
                  <div className="col-md-6">
                    <div className="profile-field">
                      <BaseInput
                        type="text"
                        name="name"
                        value={formik.values.name}
                        className={`form-control ${isEditing ? "" : "d-none"}`}
                        onChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        touched={formik.touched.name}
                        error={formik.errors.name}
                        placeholder={InputPlaceHolder(NameLabel)}
                        label={NameLabel}
                        required
                      />
                      {!isEditing && <p id="name">{profileData?.name}</p>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="profile-field">
                      <BaseInput
                        type="email"
                        name="email"
                        disabled={true}
                        label={loginLabels.Email}
                        value={formik.values.email}
                        className={`form-control ${isEditing ? "" : "d-none"}`}
                        onChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        touched={formik.touched.email}
                        error={formik.errors.email}
                        placeholder={InputPlaceHolder(EmailLabel)}
                      />
                      {!isEditing && <p id="email">{profileData?.email}</p>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="profile-field">
                      <BaseInput
                        type="text"
                        label={PhoneNumberLabel}
                        name="contact_no"
                        value={formik.values.contact_no}
                        className={`form-control ${isEditing ? "" : "d-none"}`}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value) && value.length <= 10) {
                            formik.setFieldValue("contact_no", value);
                          }
                        }}
                        handleBlur={formik.handleBlur}
                        touched={formik.touched.contact_no}
                        error={formik.errors.contact_no}
                        placeholder={InputPlaceHolder(PhoneNumberLabel)}
                        required
                      />
                      {!isEditing && (
                        <p id="phoneNumber">{profileData?.contact_no}</p>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="profile-field">
                      <BaseInput
                        type="text"
                        label={AlternatePhoneNumberLabel}
                        name="alternative_contact_no"
                        value={formik.values.alternative_contact_no}
                        className={`form-control ${isEditing ? "" : "d-none"}`}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value) && value.length <= 10) {
                            formik.setFieldValue(
                              "alternative_contact_no",
                              value
                            );
                          }
                        }}
                        handleBlur={formik.handleBlur}
                        touched={formik.touched.alternative_contact_no}
                        error={formik.errors.alternative_contact_no}
                        placeholder={InputPlaceHolder(
                          AlternatePhoneNumberLabel
                        )}
                      />
                      {!isEditing && (
                        <p id="alternateContact">
                          {profileData?.alternative_contact_no}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <h5 className="mt-3">Address</h5>
                <div className="border-bottom mb-2" />
                <div className="row">
                  <div className="col-md-4">
                    <div className="profile-field">
                      <label htmlFor="street" className="form-label">
                        {StreetLabel}
                      </label>
                      <div>
                        {!isEditing && (
                          <p id="street">{profileData?.address?.address}</p>
                        )}
                        <textarea
                          id="streetInput"
                          name="address"
                          className={`form-control ${
                            isEditing ? "" : "d-none"
                          }`}
                          value={formik.values.address}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder={InputPlaceHolder(StreetLabel)}
                        />
                        {formik.touched.address && formik.errors.address && (
                          <div className="text-danger">
                            {formik.errors.address}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="profile-field">
                      {!isEditing ? (
                        <>
                          <label htmlFor="country" className="form-label">
                            {CountryLabel}
                          </label>
                          <p id="country">
                            {profileData?.address?.primaryCountry?.country_name}
                          </p>
                        </>
                      ) : (
                        <BaseSelect
                          name="country_id"
                          label={CountryLabel}
                          options={countries}
                          value={formik.values.country_id}
                          placeholder={SelectPlaceHolder(CountryLabel)}
                          handleChange={(field: any, value: any) => {
                            formik.setFieldValue(field, value);
                            fetchState(value);
                          }}
                          handleBlur={formik.handleBlur}
                          error={
                            formik.touched.country_id &&
                            formik.errors.country_id
                          }
                          touched={formik.touched.country_id}
                          onChange={formik.handleChange}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="profile-field">
                      {!isEditing ? (
                        <>
                          <label htmlFor="state" className="form-label">
                            {StateLabel}
                          </label>
                          <p id="state">
                            {profileData?.address?.primaryState?.state_name}
                          </p>
                        </>
                      ) : (
                        <BaseSelect
                          name="state_id"
                          label={StateLabel}
                          options={states}
                          value={formik.values.state_id}
                          placeholder={SelectPlaceHolder(StateLabel)}
                          handleChange={(field: any, value: any) => {
                            formik.setFieldValue(field, value);
                            fetchCity(value);
                          }}
                          handleBlur={formik.handleBlur}
                          error={formik.errors.state_id}
                          touched={
                            formik.touched.state_id && formik.touched.state_id
                          }
                          onChange={formik.handleChange}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-4 ">
                    <div className="profile-field">
                      {!isEditing ? (
                        <>
                          <label htmlFor="city" className="form-label">
                            {CityLabel}
                          </label>
                          <p id="city">
                            {profileData?.address?.primaryCity?.city_name}
                          </p>
                        </>
                      ) : (
                        <BaseSelect
                          name="city_id"
                          label={CityLabel}
                          options={city}
                          value={formik.values.city_id}
                          placeholder={SelectPlaceHolder(CityLabel)}
                          handleChange={(field: any, value: any) => {
                            formik.setFieldValue(field, value);
                          }}
                          handleBlur={formik.handleBlur}
                          error={formik.errors.city_id}
                          touched={formik.touched.city_id}
                          onChange={formik.handleChange}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="profile-field">
                      <BaseInput
                        type="number"
                        label={PostalCodeLabel}
                        maxLength={6}
                        name="postal_code"
                        value={formik.values.postal_code}
                        className={`form-control ${isEditing ? "" : "d-none"}`}
                        onChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        touched={formik.touched.postal_code}
                        error={formik.errors.postal_code}
                        placeholder={InputPlaceHolder(PostalCodeLabel)}
                      />
                      {!isEditing && (
                        <p id="postalCode">
                          {profileData?.address?.postal_code}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-12 d-flex gap-2 justify-content-end">
                      <BaseButton
                        className={`btn btn-success ${
                          !isEditing ? "d-none" : ""
                        } mt-4`}
                        onClick={formik.handleSubmit}
                        loader={profileLoader}
                        size={"md"}
                        label="Update"
                        disable={
                          !hasFormChanged() || !formik.isValid || profileLoader
                        }
                      >
                        {profileLoader ? <Loader /> : saveButton}
                      </BaseButton>
                      <BaseButton
                        className={`btn btn-danger ${
                          !isEditing ? "d-none" : ""
                        } mt-4`}
                        onClick={toggleEdit}
                        size={"md"}
                      >
                        {cancelLabel}
                      </BaseButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
